import { useCallback } from 'react';
import type { SignalRMessage } from 'types';
import { useSignalR } from 'contexts';

const DEFAULT_TIMEOUT_MS = 30000;

export interface WaitForIncidentSyncOptions {
  incidentId: string;
  timeoutMs?: number;
  predicate?: (message: SignalRMessage) => boolean;
}

/**
 * Waits for a SignalR incident update for a specific incident after a Sentinel mutation request.
 */
export function useIncidentSync(): (options: WaitForIncidentSyncOptions) => Promise<void> {
  const { subscribe } = useSignalR();

  return useCallback(
    ({ incidentId, timeoutMs = DEFAULT_TIMEOUT_MS, predicate }: WaitForIncidentSyncOptions) =>
      new Promise<void>((resolve, reject) => {
        let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

        const unsubscribe = subscribe((message) => {
          if (message.incidentId !== incidentId) {
            return;
          }

          if (predicate && !predicate(message)) {
            return;
          }

          if (timeoutHandle) {
            clearTimeout(timeoutHandle);
          }

          unsubscribe();
          resolve();
        });

        timeoutHandle = setTimeout(() => {
          unsubscribe();
          reject(new Error('Timed out waiting for Sentinel confirmation update.'));
        }, timeoutMs);
      }),
    [subscribe],
  );
}
